import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Switch,
  FormControl,
  FormLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
  Tooltip,
  Skeleton,
  SkeletonText,
  StatArrow,
  Divider,
  Icon,
} from '@chakra-ui/react'
import { FiActivity, FiTrendingUp, FiTrendingDown, FiMinus, FiAlertCircle, FiDollarSign, FiSend, FiPackage, FiCpu } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { economyService } from '@/services/admin/economyService'
import { formatDistanceToNow } from 'date-fns'
import { useInterval } from '@/hooks/useInterval'
import { useApiError } from '@/hooks/useApiError'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/contexts/AuthContext'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { formatChainName, isChainSupported } from '@/constants/chains'
import { ChainBadge } from '@/components/common/ChainBadge'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface GasPriceData {
  success?: boolean
  error?: string
  level?: 'LOW' | 'MEDIUM' | 'HIGH'
  gasPrice?: {
    slow?: string
    standard?: string
    fast?: string
  }
  timestamp?: number
  source?: string
  nativeTokenPrice?: number // USD price of native token (ETH/BNB)
  gasCostUsd?: {
    transfer: number      // 21,000 gas - normal transfer
    erc20: number        // 65,000 gas - token transfer
    contract: number     // 150,000 gas - contract interaction
  }
}

const CHAIN_NAMES: Record<string, string> = {
  '1': 'Ethereum',
  '56': 'BNB Smart Chain',
  '8453': 'Base',
  '97': 'BNB Smart Chain Testnet',
  '84532': 'Base Sepolia',
  '11155111': 'ETH Sepolia',
  '137': 'Polygon',
  '80002': 'Polygon Amoy',
}

const NATIVE_TOKENS: Record<string, string> = {
  '1': 'ETH',
  '56': 'BNB',
  '8453': 'ETH',
  '97': 'BNB',
  '84532': 'ETH',
  '11155111': 'ETH',
  '137': 'MATIC',
  '80002': 'MATIC',
}

// Gas units for different operations
const GAS_UNITS = {
  transfer: 21000,    // Normal ETH/BNB transfer
  erc20: 65000,      // ERC20/BEP20 token transfer
  contract: 150000,   // Complex contract interaction
}

const getPrimaryGasPrice = (gasPrice?: GasPriceData['gasPrice']): string | null => {
  if (!gasPrice) return null
  return gasPrice.standard ?? gasPrice.fast ?? gasPrice.slow ?? null
}

// Convert Gwei to native token (ETH/BNB)
const gweiToNative = (gwei?: string | number | null): string => {
  if (gwei === null || gwei === undefined) return '--'
  const gweiNum = typeof gwei === 'string' ? parseFloat(gwei) : gwei
  if (Number.isNaN(gweiNum)) return '--'
  const native = gweiNum / 1_000_000_000 // 1 ETH/BNB = 10^9 Gwei
  
  // Format with appropriate decimal places
  if (native < 0.000001) {
    return native.toExponential(2)
  } else if (native < 0.001) {
    return native.toFixed(9).replace(/\.?0+$/, '')
  } else if (native < 1) {
    return native.toFixed(6).replace(/\.?0+$/, '')
  } else {
    return native.toFixed(4).replace(/\.?0+$/, '')
  }
}

// Calculate gas cost in USD
const calculateGasCostUsd = (
  gasPrice: string | number | null | undefined,
  gasUnits: number,
  tokenPrice?: number
): number | null => {
  if (gasPrice === null || gasPrice === undefined) return null
  if (!tokenPrice || tokenPrice <= 0) return null
  const gweiNum = typeof gasPrice === 'string' ? parseFloat(gasPrice) : gasPrice
  if (Number.isNaN(gweiNum)) return null
  const costInNative = (gweiNum * gasUnits) / 1_000_000_000
  return costInNative * tokenPrice
}

// Format USD price with appropriate decimal places
const formatUsdPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined) return '--'
  
  if (price < 0.01) {
    return `$${price.toFixed(4)}`
  } else if (price < 1) {
    return `$${price.toFixed(3)}`
  } else if (price < 100) {
    return `$${price.toFixed(2)}`
  } else {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

// Get price trend for USD price
const getUsdPriceTrend = (current?: number, previous?: number): 'up' | 'down' | 'stable' => {
  if (!current || !previous) return 'stable'
  const percentChange = ((current - previous) / previous) * 100
  if (percentChange > 1) return 'up'
  if (percentChange < -1) return 'down'
  return 'stable'
}

export const GasPriceMonitor: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { checkPermission } = useAuth()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const cardBg = useColorModeValue('gray.50', 'gray.900')
  const isMobile = useBreakpointValue({ base: true, md: false })

  const [gasPrices, setGasPrices] = useState<Record<string, GasPriceData>>({})
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null)
  const [priceHistory, setPriceHistory] = useState<Record<string, number[]>>({})
  const [usdPriceHistory, setUsdPriceHistory] = useState<Record<string, number[]>>({}) // Track USD price history
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshCountdown, setRefreshCountdown] = useState(300) // 5 minutes in seconds
  const [error, setError] = useState<any>(null)
  const canRefresh = checkPermission('economy:gas_refresh')
  const formatErrorMessage = (err: any): string => {
    if (!err) {
      return 'Unknown error occurred'
    }
    if (err.name === 'AbortError') {
      return 'Request timeout. The gas price service may be experiencing delays.'
    }
    const responseError = err.response?.data?.error ?? err.response?.data
    if (typeof responseError === 'string') {
      return responseError
    }
    if (responseError?.message) {
      return responseError.message
    }
    if (err.message) {
      return err.message
    }
    return 'Unknown error occurred'
  }

  const updatePriceHistory = useCallback((newPrices: Record<string, GasPriceData>) => {
    setPriceHistory(prev => {
      const updated = { ...prev }
      Object.entries(newPrices).forEach(([chainId, data]) => {
        if (data.success === false) {
          return
        }
        const latestPrice = getPrimaryGasPrice(data.gasPrice)
        if (latestPrice === null) {
          return
        }
        const currentPrice = parseFloat(latestPrice)
        if (Number.isNaN(currentPrice)) {
          return
        }
        if (!updated[chainId]) {
          updated[chainId] = [currentPrice]
        } else {
          updated[chainId] = [...updated[chainId].slice(-2), currentPrice]
        }
      })
      return updated
    })
    
    // Update USD price history
    setUsdPriceHistory(prev => {
      const updated = { ...prev }
      Object.entries(newPrices).forEach(([chainId, data]) => {
        if (data.success === false) {
          return
        }
        if (data.nativeTokenPrice) {
          if (!updated[chainId]) {
            updated[chainId] = [data.nativeTokenPrice]
          } else {
            updated[chainId] = [...updated[chainId].slice(-2), data.nativeTokenPrice]
          }
        }
      })
      return updated
    })
  }, [])

  const applyGasPriceResponse = useCallback((response: Record<string, GasPriceData>) => {
    const normalized: Record<string, GasPriceData> = {}
    Object.entries(response ?? {}).forEach(([chainId, payload]) => {
      if (payload && Object.keys(payload).length > 0) {
        normalized[chainId] = payload
      } else {
        normalized[chainId] = { success: false }
      }
    })

    updatePriceHistory(normalized)
    setGasPrices(normalized)
    setLastRefreshTime(new Date())
    setRefreshCountdown(300)
    setError(null)
  }, [updatePriceHistory])

  const mutation = useMutation({
    mutationFn: async () => {
      return await economyService.refreshGasPrices(undefined, currentEnv)
    },
    onSuccess: (response) => {
      if (response) {
        applyGasPriceResponse(response)
        
        // Add to operation history if available
        if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
          (window as any).addOperationHistory({
            type: 'gasRefresh',
            operator: 'System',
            result: 'success',
            details: 'Gas prices refreshed successfully'
          })
        }
        
        toast.success({
          title: t('monitoring.gasPrice.success.title'),
          description: t('monitoring.gasPrice.success.description'),
          duration: 3000,
          isClosable: true,
          id: `gas-price-refresh-success-${Date.now()}`, // Use timestamp for unique ID
        })
      }
    },
    onError: (err: any) => {
      setError(err)
      
      // Add to operation history if available
      if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
        (window as any).addOperationHistory({
          type: 'gasRefresh',
          operator: 'System',
          result: 'failed',
          details: err.message || 'Failed to refresh gas prices'
        })
      }
      
      // Use the common error handler
      handleError(err, 'Gas price refresh')
    },
  })

  const { mutate } = mutation

  const loadSnapshot = useCallback(async () => {
    try {
      const response = await economyService.getGasPricesMonitor(currentEnv)
      applyGasPriceResponse(response)
    } catch (err: any) {
      setError(err)
    }
  }, [applyGasPriceResponse, currentEnv])

  const handleRefresh = useCallback(() => {
    if (canRefresh) {
      mutate()
    } else {
      void loadSnapshot()
    }
  }, [canRefresh, loadSnapshot, mutate])

  const resetState = useCallback(() => {
    setGasPrices({})
    setPriceHistory({})
    setUsdPriceHistory({})
    setLastRefreshTime(null)
    setError(null)
    setRefreshCountdown(300)
  }, [])

  useEffect(() => {
    // Avoid cross-network bleed when switching mainnet/testnet.
    resetState()
    void loadSnapshot()
  }, [currentEnv, loadSnapshot, resetState])

  // Auto-refresh countdown
  useInterval(() => {
    if (autoRefresh && !mutation.isPending) {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          if (canRefresh) {
            mutation.mutate()
          } else {
            void loadSnapshot()
          }
          return 300
        }
        return prev - 1
      })
    }
  }, autoRefresh ? 1000 : null)

  // Calculate price trend
  const getPriceTrend = (chainId: string) => {
    const history = priceHistory[chainId]
    if (!history || history.length < 2) return 'stable'
    
    const current = history[history.length - 1]
    const previous = history[history.length - 2]
    const threshold = 0.5 // 0.5 gwei threshold for change
    
    if (current > previous + threshold) return 'rising'
    if (current < previous - threshold) return 'falling'
    return 'stable'
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'rising':
        return <FiTrendingUp color="var(--chakra-colors-red-500)" />
      case 'falling':
        return <FiTrendingDown color="var(--chakra-colors-green-500)" />
      default:
        return <FiMinus color="var(--chakra-colors-gray-500)" />
    }
  }

  const getTrendText = (trend: string) => {
    switch (trend) {
      case 'rising':
        return t('gasPrice.trend.rising')
      case 'falling':
        return t('gasPrice.trend.falling')
      default:
        return t('gasPrice.trend.stable')
    }
  }

  const normalizeLevel = (level?: string | null): 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN' => {
    if (!level) {
      return 'UNKNOWN'
    }
    const upper = level.toUpperCase()
    if (upper === 'LOW' || upper === 'MEDIUM' || upper === 'HIGH') {
      return upper as 'LOW' | 'MEDIUM' | 'HIGH'
    }
    return 'UNKNOWN'
  }

  const getLevelColor = (level?: string | null) => {
    switch (normalizeLevel(level)) {
      case 'LOW':
        return 'green'
      case 'MEDIUM':
        return 'yellow'
      case 'HIGH':
        return 'red'
      default:
        return 'gray'
    }
  }

  const formatGasPrice = (price?: string | null) => {
    if (price === null || price === undefined || price === '') {
      return `-- ${t('monitoring.gasPrice.gwei')}`
    }
    return `${price} ${t('monitoring.gasPrice.gwei')}`
  }

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '-'
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    } catch {
      return '-'
    }
  }

  const getChainLabel = (chainId: string) => {
    const key = `monitoring.gasPrice.chains.${chainId}`
    const translated = t(key)
    if (translated && translated !== key) {
      return translated
    }
    if (isChainSupported(chainId)) {
      return formatChainName(chainId, 'full')
    }
    return CHAIN_NAMES[chainId] || `Chain ${chainId}`
  }

  const renderChainBadge = (chainId: string) => {
    const registryLabel = isChainSupported(chainId)
      ? formatChainName(chainId, 'short')
      : getChainLabel(chainId)

    return (
      <ChainBadge
        chainId={registryLabel}
        chainCode={registryLabel}
        chainNumericId={chainId}
        isTestnet={currentEnv === 'testnet'}
      />
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {error && !mutation.isPending && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Failed to load gas prices</AlertTitle>
            <AlertDescription>
              {formatErrorMessage(error)}
              {error.response?.status && (
                <Text fontSize="sm" mt={1}>
                  Error Code: {error.response.status}
                </Text>
              )}
            </AlertDescription>
          </Box>
          <Button 
            size="sm" 
            onClick={() => {
              setError(null)
              handleRefresh()
            }}
            ml={4}
          >
            Retry
          </Button>
        </Alert>
      )}
      
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
          <Text fontSize="lg" fontWeight="semibold" minW={0} overflowWrap="anywhere">
            {t('monitoring.gasPrice.title')}
          </Text>
          <RefreshIconButton
            label={
              mutation.isPending
                ? t('monitoring.gasPrice.button.loading')
                : t('monitoring.gasPrice.button.refresh')
            }
            onClick={handleRefresh}
            isLoading={mutation.isPending}
          />
        </HStack>

        <HStack justify="space-between" align={{ base: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={2}>
          <FormControl display="flex" alignItems="center" width="auto">
            <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm">
              {t('gasPrice.autoRefresh')}
            </FormLabel>
            <Switch
              id="auto-refresh"
              isChecked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              size="sm"
              colorScheme="blue"
            />
          </FormControl>
          {autoRefresh && (
            <Text fontSize="xs" color="gray.500">
              {t('gasPrice.nextRefresh', { seconds: refreshCountdown })}
            </Text>
          )}
        </HStack>
      </VStack>

      {Object.keys(gasPrices).length === 0 && !mutation.isPending ? (
        <Box
          bg={bgColor}
          p={8}
          borderRadius="md"
          borderWidth={1}
          borderColor={borderColor}
          textAlign="center"
        >
          <Box display="inline-block" mb={4}>
            <FiAlertCircle size={32} color="var(--chakra-colors-gray-400)" />
          </Box>
          <Text color="gray.500" mb={4}>No gas price data available</Text>
          <RefreshIconButton
            label={t('monitoring.gasPrice.button.refresh')}
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
          />
        </Box>
      ) : mutation.isPending && Object.keys(gasPrices).length === 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
          {[1, 2].map((index) => (
            <Box
              key={index}
              bg={cardBg}
              p={6}
              borderRadius="lg"
              borderWidth={1}
              borderColor={borderColor}
            >
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Skeleton height="24px" width="100px" />
                  <Skeleton height="24px" width="60px" />
                </HStack>
                <SimpleGrid columns={3} spacing={2}>
                  {[1, 2, 3].map((i) => (
                    <Box key={i}>
                      <SkeletonText noOfLines={2} spacing={2} />
                    </Box>
                  ))}
                </SimpleGrid>
                <SkeletonText noOfLines={3} spacing={2} />
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        isMobile ? (
          <Accordion allowMultiple>
            {Object.entries(gasPrices).map(([chainId, data]) => {
              const trend = getPriceTrend(chainId)
              const usdTrend = getUsdPriceTrend(
                data.nativeTokenPrice,
                usdPriceHistory[chainId]?.[usdPriceHistory[chainId].length - 2]
              )
              const standardGasPrice = getPrimaryGasPrice(data.gasPrice)
              const nativeToken = NATIVE_TOKENS[chainId]
              const nativeGasDisplay = nativeToken
                ? `${gweiToNative(standardGasPrice)} ${nativeToken}`
                : gweiToNative(standardGasPrice)
              const levelKey = normalizeLevel(data.level)
              return (
                <AccordionItem key={chainId} border="1px" borderColor={borderColor} borderRadius="md" mb={2}>
                  <h2>
                    <AccordionButton gap={2}>
                      <Box flex="1" minW={0} textAlign="left">
                        <HStack spacing={3} flexWrap="wrap">
                          {renderChainBadge(chainId)}
                          <Badge colorScheme={getLevelColor(levelKey)} size="sm">
                            {t(`monitoring.gasPrice.level.${levelKey}`)}
                          </Badge>
                          {getTrendIcon(trend)}
                        </HStack>
                        <HStack fontSize="sm" color="gray.500" mt={1} spacing={2} flexWrap="wrap">
                          <Text>{formatGasPrice(standardGasPrice)}</Text>
                          <Text>•</Text>
                          <Text>{nativeGasDisplay}</Text>
                          {data.nativeTokenPrice && (
                            <>
                              <Text>•</Text>
                              <Text>{formatUsdPrice(data.nativeTokenPrice)}</Text>
                            </>
                          )}
                        </HStack>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <VStack spacing={3} align="stretch">
                      <VStack align="start" spacing={2}>
                        <HStack spacing={4}>
                          <Stat size="sm">
                            <StatLabel fontSize="xs" color="gray.500">
                              {t('monitoring.gasPrice.gasPrice')}
                            </StatLabel>
                            <StatNumber fontSize="md" fontWeight="bold">
                              {formatGasPrice(standardGasPrice)}
                            </StatNumber>
                          </Stat>
                          <Stat size="sm">
                            <StatLabel fontSize="xs" color="gray.500">
                              {t('monitoring.gasPrice.nativeToken')}
                            </StatLabel>
                            <StatNumber fontSize="md" fontWeight="bold">
                              {nativeGasDisplay}
                            </StatNumber>
                          </Stat>
                        </HStack>
                        
                        {/* USD Price Section */}
                        <Divider my={2} />
                        <VStack align="stretch" spacing={2}>
                          <Stat size="sm">
                            <StatLabel fontSize="xs" color="gray.500">
                              <HStack spacing={1}>
                                <Icon as={FiDollarSign} boxSize={3} />
                                <Text>{t('monitoring.gasPrice.tokenPrice')}</Text>
                              </HStack>
                            </StatLabel>
                            <StatNumber fontSize="md" fontWeight="bold">
                              {formatUsdPrice(data.nativeTokenPrice)}
                              {usdTrend !== 'stable' && (
                                <StatArrow 
                                  type={usdTrend === 'up' ? 'increase' : 'decrease'} 
                                  ml={2}
                                />
                              )}
                            </StatNumber>
                          </Stat>
                          
                          {/* Gas Cost in USD */}
                          {data.gasCostUsd ? (
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={2}>
                                {t('monitoring.gasPrice.gasCostUsd')}:
                              </Text>
                              <SimpleGrid columns={1} spacing={1}>
                                <HStack justify="space-between">
                                  <HStack spacing={1}>
                                    <Icon as={FiSend} boxSize={3} />
                                    <Text fontSize="xs">{t('monitoring.gasPrice.transfer')}</Text>
                                  </HStack>
                                  <Text fontSize="xs" fontWeight="medium">
                                    {formatUsdPrice(data.gasCostUsd.transfer)}
                                  </Text>
                                </HStack>
                                <HStack justify="space-between">
                                  <HStack spacing={1}>
                                    <Icon as={FiPackage} boxSize={3} />
                                    <Text fontSize="xs">{t('monitoring.gasPrice.tokenTransfer')}</Text>
                                  </HStack>
                                  <Text fontSize="xs" fontWeight="medium">
                                    {formatUsdPrice(data.gasCostUsd.erc20)}
                                  </Text>
                                </HStack>
                                <HStack justify="space-between">
                                  <HStack spacing={1}>
                                    <Icon as={FiCpu} boxSize={3} />
                                    <Text fontSize="xs">{t('monitoring.gasPrice.contractInteraction')}</Text>
                                  </HStack>
                                  <Text fontSize="xs" fontWeight="medium">
                                    {formatUsdPrice(data.gasCostUsd.contract)}
                                  </Text>
                                </HStack>
                              </SimpleGrid>
                            </Box>
                          ) : (
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={2}>
                                {t('monitoring.gasPrice.gasCostUsd')}:
                              </Text>
                              <SimpleGrid columns={1} spacing={1}>
                                {['transfer', 'erc20', 'contract'].map((type) => (
                                  <HStack key={type} justify="space-between">
                                    <HStack spacing={1}>
                                      <Icon 
                                        as={type === 'transfer' ? FiSend : type === 'erc20' ? FiPackage : FiCpu} 
                                        boxSize={3} 
                                        color="gray.500"
                                      />
                                      <Text fontSize="xs" color="gray.600">
                                        {t(`monitoring.gasPrice.${type === 'erc20' ? 'tokenTransfer' : type === 'contract' ? 'contractInteraction' : type}`)}
                                      </Text>
                                    </HStack>
                                    <Text fontSize="xs" fontWeight="medium">
                                      {formatUsdPrice(
                                        calculateGasCostUsd(
                                          standardGasPrice,
                                          GAS_UNITS[type as keyof typeof GAS_UNITS],
                                          data.nativeTokenPrice
                                        )
                                      )}
                                    </Text>
                                  </HStack>
                                ))}
                              </SimpleGrid>
                            </Box>
                          )}
                        </VStack>
                      </VStack>
                      
                      <HStack spacing={2} fontSize="xs">
                        <Text color="gray.500">{t('gasPrice.trend.label')}:</Text>
                        <Badge colorScheme={trend === 'rising' ? 'red' : trend === 'falling' ? 'green' : 'gray'}>
                          {getTrendText(trend)}
                        </Badge>
                      </HStack>
                      
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="gray.500">
                            {t('monitoring.gasPrice.source')}:
                          </Text>
                          <Text fontSize="xs" fontWeight="medium">
                            {data.source}
                          </Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="gray.500">
                            {t('monitoring.gasPrice.lastUpdate')}:
                          </Text>
                          <Text fontSize="xs" fontWeight="medium">
                            {formatTimestamp(data.timestamp)}
                          </Text>
                        </HStack>
                      </VStack>
                      {data.success === false && (
                        <Text fontSize="xs" color="red.400">
                          {data.error || t('monitoring.gasPrice.unavailable')}
                        </Text>
                      )}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              )
            })}
          </Accordion>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
            {Object.entries(gasPrices).map(([chainId, data]) => {
              const trend = getPriceTrend(chainId)
              const usdTrend = getUsdPriceTrend(
                data.nativeTokenPrice,
                usdPriceHistory[chainId]?.[usdPriceHistory[chainId].length - 2]
              )
              const standardGasPrice = getPrimaryGasPrice(data.gasPrice)
              const nativeToken = NATIVE_TOKENS[chainId]
              const nativeGasDisplay = nativeToken
                ? `${gweiToNative(standardGasPrice)} ${nativeToken}`
                : gweiToNative(standardGasPrice)
              const levelKey = normalizeLevel(data.level)
              return (
                <Box
                  key={chainId}
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  borderWidth={1}
                  borderColor={borderColor}
                  position="relative"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }}
                >
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1}>
                    {renderChainBadge(chainId)}
                    <HStack spacing={2}>
                      <Badge
                        colorScheme={getLevelColor(levelKey)}
                        fontSize="sm"
                        px={2}
                        py={1}
                      >
                        {t(`monitoring.gasPrice.level.${levelKey}`)}
                      </Badge>
                      <Tooltip label={getTrendText(trend)}>
                        <Box display="inline-block">
                          {getTrendIcon(trend)}
                        </Box>
                      </Tooltip>
                    </HStack>
                  </VStack>
                  <Box>
                    <FiActivity size={24} color={`var(--chakra-colors-${getLevelColor(levelKey)}-500)`} />
                  </Box>
                </HStack>

                <HStack spacing={6}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs" color="gray.500">
                      {t('monitoring.gasPrice.gasPrice')}
                    </StatLabel>
                    <StatNumber fontSize="lg" fontWeight="bold">
                      {formatGasPrice(standardGasPrice)}
                    </StatNumber>
                  </Stat>
                  <Stat size="sm">
                    <StatLabel fontSize="xs" color="gray.500">
                      {t('monitoring.gasPrice.nativeToken')}
                    </StatLabel>
                    <StatNumber fontSize="lg" fontWeight="bold">
                      {nativeGasDisplay}
                    </StatNumber>
                  </Stat>
                </HStack>

                {/* USD Price Section */}
                <Divider />
                <VStack align="stretch" spacing={3}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs" color="gray.500">
                      <HStack spacing={1}>
                        <Icon as={FiDollarSign} boxSize={3} />
                        <Text>{t('monitoring.gasPrice.tokenPrice')}</Text>
                      </HStack>
                    </StatLabel>
                    <HStack>
                      <StatNumber fontSize="lg" fontWeight="bold">
                        {formatUsdPrice(data.nativeTokenPrice)}
                      </StatNumber>
                      {usdTrend !== 'stable' && (
                        <StatArrow 
                          type={usdTrend === 'up' ? 'increase' : 'decrease'} 
                        />
                      )}
                    </HStack>
                  </Stat>
                  
                  {/* Gas Cost in USD */}
                  {data.gasCostUsd ? (
                    <Box>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        {t('monitoring.gasPrice.gasCostUsd')}:
                      </Text>
                      <SimpleGrid columns={3} spacing={2}>
                        <Tooltip label={`${GAS_UNITS.transfer.toLocaleString()} gas`}>
                          <VStack align="start" spacing={0}>
                            <HStack spacing={1}>
                              <Icon as={FiSend} boxSize={3} color="gray.500" />
                              <Text fontSize="xs" color="gray.600">
                                {t('monitoring.gasPrice.transfer')}
                              </Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="semibold">
                              {formatUsdPrice(data.gasCostUsd.transfer)}
                            </Text>
                          </VStack>
                        </Tooltip>
                        <Tooltip label={`${GAS_UNITS.erc20.toLocaleString()} gas`}>
                          <VStack align="start" spacing={0}>
                            <HStack spacing={1}>
                              <Icon as={FiPackage} boxSize={3} color="gray.500" />
                              <Text fontSize="xs" color="gray.600">
                                {t('monitoring.gasPrice.tokenTransfer')}
                              </Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="semibold">
                              {formatUsdPrice(data.gasCostUsd.erc20)}
                            </Text>
                          </VStack>
                        </Tooltip>
                        <Tooltip label={`${GAS_UNITS.contract.toLocaleString()} gas`}>
                          <VStack align="start" spacing={0}>
                            <HStack spacing={1}>
                              <Icon as={FiCpu} boxSize={3} color="gray.500" />
                              <Text fontSize="xs" color="gray.600">
                                {t('monitoring.gasPrice.contractInteraction')}
                              </Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="semibold">
                              {formatUsdPrice(data.gasCostUsd.contract)}
                            </Text>
                          </VStack>
                        </Tooltip>
                      </SimpleGrid>
                    </Box>
                  ) : (
                    <Box>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        {t('monitoring.gasPrice.gasCostUsd')}:
                      </Text>
                      <SimpleGrid columns={3} spacing={2}>
                        {['transfer', 'erc20', 'contract'].map((type) => (
                          <VStack key={type} align="start" spacing={0}>
                            <HStack spacing={1}>
                              <Icon 
                                as={type === 'transfer' ? FiSend : type === 'erc20' ? FiPackage : FiCpu} 
                                boxSize={3} 
                                color="gray.500" 
                              />
                              <Text fontSize="xs" color="gray.600">
                                {t(`monitoring.gasPrice.${type === 'erc20' ? 'tokenTransfer' : type === 'contract' ? 'contractInteraction' : type}`)}
                              </Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="semibold">
                              {formatUsdPrice(
                                calculateGasCostUsd(
                                  standardGasPrice,
                                  GAS_UNITS[type as keyof typeof GAS_UNITS],
                                  data.nativeTokenPrice
                                )
                              )}
                            </Text>
                          </VStack>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </VStack>

                <VStack align="start" spacing={1}>
                  <HStack spacing={2}>
                    <Text fontSize="xs" color="gray.500">
                      {t('gasPrice.trend.label')}:
                    </Text>
                    <Badge
                      colorScheme={trend === 'rising' ? 'red' : trend === 'falling' ? 'green' : 'gray'}
                      fontSize="xs"
                    >
                      {getTrendText(trend)}
                    </Badge>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="xs" color="gray.500">
                      {t('monitoring.gasPrice.source')}:
                    </Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {data.source}
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="xs" color="gray.500">
                      {t('monitoring.gasPrice.lastUpdate')}:
                    </Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {formatTimestamp(data.timestamp)}
                    </Text>
                  </HStack>
                </VStack>
                {data.success === false && (
                  <Text fontSize="xs" color="red.400">
                    {data.error || t('monitoring.gasPrice.unavailable')}
                  </Text>
                )}
              </VStack>
            </Box>
              )
            })}
          </SimpleGrid>
        )
      )}

      {lastRefreshTime && (
        <Box textAlign="center" color="gray.500" fontSize="sm">
          Last refreshed {formatDistanceToNow(lastRefreshTime, { addSuffix: true })}
        </Box>
      )}
    </VStack>
  )
}
